import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getMatches,
  settleMatch,
  getMatchResults,
} from "../../services/matchService";
import { getLeagues } from "../../services/leagueService";

const toastCfg = { autoClose: 1000 };

/* ─── Helpers ─── */
const getStatusBadge = (status) => {
  if (!status) return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  const s = status.toLowerCase();
  switch (s) {
    case "completed": return { bg: "#dcfce7", color: "#16a34a", border: "#bbf7d0" };
    case "live": return { bg: "#fee2e2", color: "#ef4444", border: "#fecaca" };
    case "upcoming": return { bg: "#fef3c7", color: "#d97706", border: "#fde68a" };
    default: return { bg: "#f1f5f9", color: "#64748b", border: "#e2e8f0" };
  }
};

const SortIcon = ({ col }) => {
  if (!col.getCanSort()) return null;
  const dir = col.getIsSorted();
  return (
    <span style={{ marginLeft: 4, opacity: dir ? 1 : 0.35, fontSize: 11 }}>
      {dir === "asc" ? "▲" : dir === "desc" ? "▼" : "⇅"}
    </span>
  );
};

/* ─── Settle Match Modal ─── */
const SettleModal = ({ show, onHide, onSuccess, leagues, matches }) => {
  const [leagueId, setLeagueId] = useState("");
  const [matchId, setMatchId] = useState("");
  const [winnerSide, setWinnerSide] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setLeagueId("");
      setMatchId("");
      setWinnerSide("");
    }
  }, [show]);

  const filteredMatches = useMemo(() => {
    if (!leagueId) return [];
    return matches.filter((m) => String(m.league_id) === String(leagueId));
  }, [matches, leagueId]);

  const handleSubmit = async () => {
    if (!leagueId || !matchId || !winnerSide) {
      toast.error("League, Match & Winner are required", toastCfg);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        results: [
          {
            match_id: Number(matchId),
            league_id: Number(leagueId),
            winner_side: winnerSide,
          },
        ],
      };

      const res = await settleMatch(payload);

      if (res?.status) {
        toast.success("Match settled successfully", toastCfg);
        onSuccess();
        onHide();
      } else {
        toast.error(res?.message || "Failed to settle match", toastCfg);
      }
    } catch {
      toast.error("Server error", toastCfg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop="static" size="md">
      <div style={ms.header}>
        <div>
          <div style={ms.headerTitle}>Settle Match Result</div>
          <div style={ms.headerSub}>Declare the winning side for a fixture</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide} disabled={saving}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={ms.body}>
        <div style={ms.grid}>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>League Division</label>
            <select
              style={ms.select}
              value={leagueId}
              onChange={(e) => { setLeagueId(e.target.value); setMatchId(""); setWinnerSide(""); }}
              disabled={saving}
            >
              <option value="">Select League...</option>
              {leagues.map((l) => (
                <option key={l.id} value={l.id}>{l.league_name}</option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Match Fixture</label>
            <select
              style={ms.select}
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
              disabled={!leagueId || saving}
            >
              <option value="">Select Match...</option>
              {filteredMatches.map((m) => (
                <option key={m.id} value={m.id}>{m.team_a} vs {m.team_b}</option>
              ))}
            </select>
          </div>

          <div style={{ gridColumn: "1 / -1" }}>
            <label style={ms.label}>Declare Winning Side</label>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                style={{
                  ...ms.winnerBtn,
                  background: winnerSide === "A" ? "#eff6ff" : "#f8fafc",
                  borderColor: winnerSide === "A" ? "#3b82f6" : "#e2e8f0",
                  color: winnerSide === "A" ? "#1d4ed8" : "#64748b",
                }}
                onClick={() => setWinnerSide("A")}
                disabled={saving || !matchId}
              >
                Team A win
              </button>
              <button
                style={{
                  ...ms.winnerBtn,
                  background: winnerSide === "B" ? "#fdf4ff" : "#f8fafc",
                  borderColor: winnerSide === "B" ? "#d946ef" : "#e2e8f0",
                  color: winnerSide === "B" ? "#a21caf" : "#64748b",
                }}
                onClick={() => setWinnerSide("B")}
                disabled={saving || !matchId}
              >
                Team B win
              </button>
            </div>
          </div>
        </div>
      </div>
      <div style={ms.footer}>
        <button style={ms.cancelBtn} onClick={onHide} disabled={saving}>Cancel</button>
        <button style={ms.approveBtn} onClick={handleSubmit} disabled={saving}>
          {saving ? <span className="spinner-border spinner-border-sm" /> : "Submit Result"}
        </button>
      </div>
    </Modal>
  );
};

/* ─── View Modal ─── */
const ViewModal = ({ show, onHide, resultData, viewMatch }) => {
  if (!resultData || !viewMatch) return null;
  const matchHdr = viewMatch.match_header || {};
  const det = viewMatch.detailing || {};
  const gt = resultData.grand_total || {};
  const logs = resultData.user_logs || [];

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <div style={{ ...ms.header, paddingBottom: 20 }}>
        <div>
          <div style={ms.headerTitle}>Match Overview: #{matchHdr.id}</div>
          <div style={ms.headerSub}>{matchHdr.teams}</div>
        </div>
        <button style={ms.closeBtn} onClick={onHide}>
          <Icon icon="radix-icons:cross-2" style={{ fontSize: 18 }} />
        </button>
      </div>
      <div style={{ padding: "24px", background: "#f8fafc", maxHeight: "75vh", overflowY: "auto" }}>
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: 24 }}>
          {/* Match Details Card */}
          <div style={{ flex: "1 1 300px", background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <h6 style={{ fontWeight: 800, color: "#0f172a", marginBottom: 16 }}>Financial Breakdown</h6>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Total Received:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{det.total_received}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Refunded:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{det.refunded_amount}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Matched Pool:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{det.actual_matched_pool}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
              <span style={{ color: "#64748b", fontWeight: 600 }}>Winners Payout:</span>
              <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{det.payout_to_winners}</span>
            </div>
            <div style={{ height: 1, background: "#f1f5f9", margin: "12px 0" }} />
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.9rem" }}>
              <span style={{ color: "#0f172a", fontWeight: 700 }}>Admin P/L:</span>
              <span style={{ color: Number(det.admin_profit_loss) >= 0 ? "#16a34a" : "#dc2626", fontWeight: 800 }}>
                ₹{det.admin_profit_loss}
              </span>
            </div>
          </div>

          {/* Grand Total Link */}
          {gt.revenue && (
            <div style={{ flex: "1 1 300px", background: "#fff", borderRadius: 12, padding: 20, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", borderTop: `4px solid ${gt.result === 'PROFIT' ? '#16a34a' : '#dc2626'}` }}>
              <h6 style={{ fontWeight: 800, color: "#0f172a", marginBottom: 16 }}>Grand Statement</h6>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
                <span style={{ color: "#64748b", fontWeight: 600 }}>Revenue:</span>
                <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{gt.revenue}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
                <span style={{ color: "#64748b", fontWeight: 600 }}>Refunds:</span>
                <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{gt.refunds}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: "0.85rem" }}>
                <span style={{ color: "#64748b", fontWeight: 600 }}>Payouts:</span>
                <span style={{ color: "#1e293b", fontWeight: 800 }}>₹{gt.payouts}</span>
              </div>
              <div style={{ height: 1, background: "#f1f5f9", margin: "12px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ color: "#0f172a", fontWeight: 700, fontSize: "0.9rem" }}>Net Profit: </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ background: gt.result === 'PROFIT' ? '#dcfce7' : '#fee2e2', color: gt.result === 'PROFIT' ? '#16a34a' : '#ef4444', padding: "3px 8px", borderRadius: 100, fontSize: "0.65rem", fontWeight: 800 }}>
                    {gt.result}
                  </span>
                  <span style={{ color: "#0f172a", fontWeight: 900, fontSize: "1.1rem" }}>₹{gt.net_profit}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Bets Table */}
        <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.05)", overflow: "hidden" }}>
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #f1f5f9", background: "#f8fafc" }}>
            <h6 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>User Bets Ledger</h6>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem", textAlign: "left" }}>
              <thead>
                <tr>
                  <th style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>User</th>
                  <th style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Side</th>
                  <th style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Amount</th>
                  <th style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Payout</th>
                  <th style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Status</th>
                  <th style={{ padding: "12px 20px", background: "#fff", borderBottom: "1px solid #f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: "30px 20px", textAlign: "center", color: "#94a3b8" }}>No ledger bets found</td></tr>
                ) : (
                  logs.map((u, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f8fafc" }}>
                      <td style={{ padding: "12px 20px", color: "#1e293b", fontWeight: 600 }}>{u.user}</td>
                      <td style={{ padding: "12px 20px" }}>
                        <span style={{ fontWeight: 800, padding: "2px 8px", borderRadius: 100, fontSize: "0.7rem", background: u.side === "A" ? "#eff6ff" : "#fdf4ff", color: u.side === "A" ? "#2563eb" : "#c026d3" }}>SIDE {u.side}</span>
                      </td>
                      <td style={{ padding: "12px 20px", color: "#0f172a", fontWeight: 700 }}>₹{u.amount}</td>
                      <td style={{ padding: "12px 20px", color: "#16a34a", fontWeight: 700 }}>{u.win_payout ? `₹${u.win_payout}` : "—"}</td>
                      <td style={{ padding: "12px 20px" }}>
                        <span style={{ ...getStatusBadge(u.status), padding: "3px 9px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase", border: "1px solid", borderColor: getStatusBadge(u.status).border }}>{u.status}</span>
                      </td>
                      <td style={{ padding: "12px 20px", color: "#64748b" }}>{u.time}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </Modal>
  );
};

/* ─── Layout Styles ─── */
const ms = {
  header: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "20px 24px 16px", borderBottom: "1px solid #f1f5f9" },
  headerTitle: { fontWeight: 800, fontSize: "1.05rem", color: "#0f172a" },
  headerSub: { fontSize: "0.78rem", color: "#94a3b8", marginTop: 2 },
  closeBtn: { background: "#f1f5f9", border: "none", borderRadius: 8, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b" },
  body: { padding: "18px 24px" },
  grid: { display: "grid", gridTemplateColumns: "1fr", gap: "16px 20px" },
  label: { display: "block", fontSize: "0.75rem", fontWeight: 700, color: "#475569", marginBottom: 5, textTransform: "uppercase", letterSpacing: "0.04em" },
  select: { width: "100%", padding: "9px 12px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.87rem", color: "#1e293b", background: "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "inherit" },
  winnerBtn: { flex: 1, padding: "12px", borderRadius: 9, border: "2px solid", cursor: "pointer", fontWeight: 800, fontSize: "0.85rem", transition: "all 0.15s ease" },
  footer: { display: "flex", justifyContent: "flex-end", gap: 10, padding: "14px 24px 20px", borderTop: "1px solid #f1f5f9" },
  cancelBtn: { padding: "8px 18px", background: "#f1f5f9", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 600, color: "#475569", cursor: "pointer" },
  approveBtn: { display: "flex", alignItems: "center", gap: 6, padding: "8px 20px", background: "linear-gradient(135deg,#16632b,#1da144)", border: "none", borderRadius: 8, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", minWidth: 100, justifyContent: "center" },
};

const s = {
  root: { padding: "20px 24px 40px", background: "#f1f5f9", minHeight: "100vh", fontFamily: "'Inter', sans-serif" },
  headerWrap: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  pageTitle: { margin: 0, fontWeight: 800, fontSize: "1.5rem", color: "#0f172a" },
  pageSub: { margin: "4px 0 0", color: "#64748b", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: 8 },
  addBtn: { display: "flex", alignItems: "center", gap: 6, padding: "9px 18px", background: "#0f172a", border: "none", borderRadius: 9, fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, color: "#fff", cursor: "pointer", boxShadow: "0 2px 6px rgba(15,23,42,0.2)" },
  card: { background: "#fff", borderRadius: 16, border: "1px solid #e8edf2", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", overflow: "hidden" },
  toolbar: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 10 },
  toolbarLeft: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  toolbarRight: { display: "flex", alignItems: "center", gap: 8 },
  searchWrap: { position: "relative", display: "flex", alignItems: "center" },
  searchIcon: { position: "absolute", left: 10, fontSize: 15, color: "#94a3b8", pointerEvents: "none" },
  searchInput: { padding: "7px 30px 7px 32px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.82rem", color: "#334155", background: "#f8fafc", outline: "none", width: 220, fontFamily: "inherit" },
  clearBtn: { position: "absolute", right: 8, background: "none", border: "none", cursor: "pointer", color: "#94a3b8", padding: 0, display: "flex", alignItems: "center" },
  select: { padding: "7px 10px", border: "1.5px solid #e2e8f0", borderRadius: 9, fontSize: "0.81rem", color: "#475569", background: "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "inherit", minWidth: 120 },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" },
  th: { padding: "10px 14px", background: "#f8fafc", color: "#64748b", fontWeight: 700, fontSize: "0.71rem", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "1.5px solid #f1f5f9", whiteSpace: "nowrap", textAlign: "left" },
  td: { padding: "9px 14px", borderBottom: "1px solid #f8fafc", color: "#334155", verticalAlign: "middle" },
  emptyCell: { padding: "48px 20px", textAlign: "center", color: "#94a3b8", fontSize: "0.85rem" },
  pagination: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderTop: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 },
  pgInfo: { fontSize: "0.78rem", color: "#64748b" },
  pgBtns: { display: "flex", gap: 4 },
  pgBtn: { minWidth: 30, height: 30, border: "1px solid #e2e8f0", background: "#fff", borderRadius: 7, cursor: "pointer", fontSize: "0.78rem", color: "#475569", fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", transition: "all 0.15s" },
  pgBtnActive: { background: "linear-gradient(135deg,#16632b,#1da144)", color: "#fff", border: "none", fontWeight: 700 },
};

const ts = {
  viewBtn: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 10px", background: "#fdf4ff", color: "#c026d3", border: "1px solid #fbcfe8", borderRadius: 7, cursor: "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 600 },
};

export const MatchResultLayer = () => {
  const [data, setData] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filters
  const [leagueFilter, setLeagueFilter] = useState("all");
  const [matchFilter, setMatchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [globalFilter, setGlobalFilter] = useState("");
  
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewMatchData, setViewMatchData] = useState(null);
  const [resultData, setResultData] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [matchesRes, leaguesRes] = await Promise.all([getMatches(), getLeagues()]);
      if (matchesRes?.status) setData(matchesRes.data || []);
      if (leaguesRes?.status) setLeagues(leaguesRes.data || []);
    } catch {
      toast.error("Failed to load records", toastCfg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Derived filter options
  const matchLeagues = useMemo(() => {
    const activeLeafs = [...new Set(data.map(m => String(m.league_id)))];
    return leagues.filter(l => activeLeafs.includes(String(l.id)));
  }, [data, leagues]);

  const matchOptions = useMemo(() => {
    if (leagueFilter === "all") return data;
    return data.filter(m => String(m.league_id) === String(leagueFilter));
  }, [data, leagueFilter]);

  const statusOptions = useMemo(() => {
    return [...new Set(data.map(m => m.status).filter(Boolean))];
  }, [data]);

  // Ensure Match Dropdown resets on League change
  useEffect(() => {
    setMatchFilter("all");
    setPagination(p => ({ ...p, pageIndex: 0 }));
  }, [leagueFilter]);

  const filteredData = useMemo(() => {
    return data.filter((m) => {
      const lMatch = leagueFilter === "all" ? true : String(m.league_id) === String(leagueFilter);
      const mMatch = matchFilter === "all" ? true : String(m.id) === String(matchFilter);
      const sMatch = statusFilter === "all" ? true : m.status?.toLowerCase() === statusFilter.toLowerCase();
      return lMatch && mMatch && sMatch;
    });
  }, [data, leagueFilter, matchFilter, statusFilter]);

  const handleView = async (id) => {
    try {
      const res = await getMatchResults(id);
      if (res?.status && res.matches?.length > 0) {
        setResultData(res);
        setViewMatchData(res.matches[0]);
        setShowViewModal(true);
      } else {
        toast.error("No detailed results found", toastCfg);
      }
    } catch {
      toast.error("Failed to load details", toastCfg);
    }
  };

  const columns = useMemo(() => [
    {
      id: "serial",
      header: "#",
      size: 50,
      enableSorting: false,
      cell: ({ row }) => <span style={{ color: "#64748b" }}>{row.index + 1 + pagination.pageIndex * pagination.pageSize}</span>,
    },
    {
      id: "matchup",
      header: "Match Fixture",
      size: 350,
      cell: ({ row }) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>{row.original.team_a}</span>
          <span style={{ fontSize: "0.7rem", color: "#ef4444", fontWeight: 800 }}>VS</span>
          <span style={{ fontWeight: 700, color: "#1e293b", fontSize: "0.85rem" }}>{row.original.team_b}</span>
        </div>
      ),
    },
    {
      accessorKey: "winner_side",
      header: "Winner",
      size: 100,
      cell: ({ getValue }) => {
        const val = getValue();
        return val ? <span style={{ fontWeight: 800, color: val === 'A' ? '#2563eb' : '#c026d3' }}>TEAM {val}</span> : <span style={{ color: "#94a3b8" }}>—</span>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 150,
      cell: ({ getValue }) => {
        const val = getValue();
        const stat = getStatusBadge(val);
        return (
          <span style={{ background: stat.bg, color: stat.color, border: `1px solid ${stat.border}`, padding: "3px 9px", borderRadius: 100, fontSize: "0.68rem", fontWeight: 700, textTransform: "uppercase" }}>
            {val || "—"}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "Action",
      size: 100,
      enableSorting: false,
      cell: ({ row }) => (
        <button style={ts.viewBtn} onClick={() => handleView(row.original.id)}>
          <Icon icon="solar:eye-bold" style={{ fontSize: 13 }} /> View Result
        </button>
      ),
    },
  ], [pagination.pageIndex, pagination.pageSize]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    globalFilterFn: (row, _colId, filterValue) => {
      const v = filterValue.toLowerCase();
      return (
        row.original.team_a?.toLowerCase().includes(v) ||
        row.original.team_b?.toLowerCase().includes(v) ||
        String(row.original.id).includes(v)
      );
    },
  });

  const { pageIndex, pageSize } = table.getState().pagination;
  const pageCount = table.getPageCount();
  const rawCount = table.getFilteredRowModel().rows.length;

  return (
    <div style={s.root}>
      <div style={s.headerWrap}>
        <div>
          <h4 style={s.pageTitle}>Match Settlement</h4>
          <p style={s.pageSub}>
             Resolve completed matches and oversee payouts
          </p>
        </div>
        <button style={s.addBtn} onClick={() => setShowAddModal(true)}>
          <Icon icon="solar:check-circle-bold" style={{ fontSize: 18 }} />
          Settle Match
        </button>
      </div>

      <div style={s.card}>
        <div style={s.toolbar}>
          <div style={s.toolbarLeft}>
            <div style={s.searchWrap}>
              <Icon icon="ion:search-outline" style={s.searchIcon} />
              <input
                style={s.searchInput}
                placeholder="Search team or ID…"
                value={globalFilter}
                onChange={(e) => { setGlobalFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}
              />
              {globalFilter && (
                <button style={s.clearBtn} onClick={() => setGlobalFilter("")}>
                  <Icon icon="radix-icons:cross-2" style={{ fontSize: 12 }} />
                </button>
              )}
            </div>

            <select style={s.select} value={leagueFilter} onChange={(e) => setLeagueFilter(e.target.value)}>
              <option value="all">All Leagues</option>
              {matchLeagues.map((l) => <option key={l.id} value={l.id}>{l.league_name}</option>)}
            </select>

            <select style={s.select} value={matchFilter} onChange={(e) => { setMatchFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Matches</option>
              {matchOptions.map((m) => <option key={m.id} value={m.id}>{m.team_a} vs {m.team_b}</option>)}
            </select>

            <select style={s.select} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPagination((p) => ({ ...p, pageIndex: 0 })); }}>
              <option value="all">All Statuses</option>
              {statusOptions.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>

          <div style={s.toolbarRight}>
            <select style={s.select} value={pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}>
              {[10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>Show {n}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{
                        ...s.th,
                        width: header.column.getSize() || undefined,
                        cursor: header.column.getCanSort() ? "pointer" : "default",
                        userSelect: "none",
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon col={header.column} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center", color: "#94a3b8" }}>
                      <span className="spinner-border spinner-border-sm" />
                      Loading records…
                    </div>
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={s.emptyCell}>
                    <Icon icon="solar:folder-error-bold-duotone" style={{ fontSize: 36, color: "#cbd5e1", display: "block", margin: "0 auto 8px" }} />
                    No match fixtures found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row, idx) => (
                  <tr key={row.id}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#f0fdf4"}
                    onMouseLeave={(e) => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} style={s.td}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div style={s.pagination}>
          <div style={s.pgInfo}>
            Showing{" "}
            <strong>{rawCount > 0 ? pageIndex * pageSize + 1 : 0}–{Math.min((pageIndex + 1) * pageSize, rawCount)}</strong>
            {" "}of <strong>{rawCount}</strong> records
          </div>
          <div style={s.pgBtns}>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(0)}          disabled={!table.getCanPreviousPage()}>«</button>
            <button style={s.pgBtn} onClick={() => table.previousPage()}            disabled={!table.getCanPreviousPage()}>‹</button>
            {Array.from({ length: Math.min(pageCount, 7) }, (_, i) => {
              const MathMax = Math.max;
              const MathMin = Math.min;
              const pg = MathMax(0, MathMin(pageIndex - 3, pageCount - 7)) + i;
              if (pg >= pageCount) return null;
              return (
                <button key={pg} style={{ ...s.pgBtn, ...(pg === pageIndex ? s.pgBtnActive : {}) }}
                  onClick={() => table.setPageIndex(pg)}>
                  {pg + 1}
                </button>
              );
            })}
            <button style={s.pgBtn} onClick={() => table.nextPage()}                disabled={!table.getCanNextPage()}>›</button>
            <button style={s.pgBtn} onClick={() => table.setPageIndex(pageCount - 1)} disabled={!table.getCanNextPage()}>»</button>
          </div>
        </div>
      </div>

      <SettleModal 
        show={showAddModal} 
        onHide={() => setShowAddModal(false)}
        onSuccess={() => loadData()}
        leagues={leagues}
        matches={data}
      />

      <ViewModal 
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        resultData={resultData}
        viewMatch={viewMatchData}
      />
    </div>
  );
};